import { useQuery } from "@tanstack/react-query";

import { fetchApi } from "@/lib/api";

/** The next touch of a drip, mission or campaign (`app.services.outreach.OutreachNext`). */
export interface OutreachNext {
  channel: "email" | "text" | "phone" | string;
  /** "pay_link" for a Pay Link text. */
  kind: string | null;
  title: string | null;
  at: string | null;
  /** auto: it sends itself. sender: `who_name` sends it. owner: the deal owner. pool: any CSR in the mission. */
  who: "auto" | "sender" | "owner" | "pool";
  who_name: string | null;
  /** An email draft already sits in the sender's mailbox. */
  in_mailbox: boolean;
}

export interface OutreachItem {
  /** The row's own key: the run's id, or the campaign's. */
  key: string;
  kind: "drip" | "mission" | "campaign";
  id: string;
  name: string;
  state: "active" | "held" | "ended";
  step: number | null;
  steps: number | null;
  next: OutreachNext | null;
  /** Why it holds, or how it ended. */
  note: string | null;
  /** The drip, mission or campaign page; null when the viewer may not open it. */
  href: string | null;
}

export interface OutreachStop {
  channel: "email" | "sms" | "call";
  label: string;
}

export interface Outreach {
  items: OutreachItem[];
  stops: OutreachStop[];
}

/** The drips, missions and campaigns a deal and its customer are in (`GET /deals/{id}/outreach`). */
export function useDealOutreach(dealId: string | null | undefined) {
  return useQuery<Outreach>({
    queryKey: ["outreach", "deal", dealId],
    queryFn: ({ signal }) => fetchApi(`/deals/${dealId}/outreach`, { signal }),
    enabled: !!dealId,
    staleTime: 60 * 1000,
  });
}

/** The drips, missions and campaigns a customer is in (`GET /canonical/persons/{id}/outreach`). */
export function usePersonOutreach(personId: string | null | undefined) {
  return useQuery<Outreach>({
    queryKey: ["outreach", "person", personId],
    queryFn: ({ signal }) =>
      fetchApi(`/canonical/persons/${personId}/outreach`, { signal }),
    enabled: !!personId,
    staleTime: 60 * 1000,
  });
}
